import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Vote, Users, Clock, ExternalLink, TrendingUp, TrendingDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";

/**
 * R157C/D — Admin dashboard widget showing live turnout for every election
 * currently in nominations_open or voting_open. R157D added a sparkline
 * fed by `turnout_snapshot_loop` snapshots + a "recent slope" indicator.
 */
export default function AdminElectionTurnoutCard() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [histMap, setHistMap] = useState({});     // eid → [{at, turnout_pct}]
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async (eid) => {
    try {
      const { data } = await api.get(`/admin/board/elections/${eid}/turnout-history`);
      setHistMap((m) => ({ ...m, [eid]: data.points || [] }));
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/AdminElectionTurnoutCard.jsx: swallowed', err); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/board/elections/turnout",
          { params: propertyId ? { property_id: propertyId } : {} });
        if (cancelled) return;
        const list = data.items || [];
        setItems(list);
        // Fetch histories in parallel
        await Promise.all(list.map((e) => loadHistory(e.id)));
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/AdminElectionTurnoutCard.jsx: swallowed', err); }
      if (!cancelled) setLoading(false);
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [propertyId, loadHistory]);

  if (loading || items.length === 0) return null;

  return (
    <Card className="p-5 bg-card border-white/10" data-testid="admin-election-turnout-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Vote className="h-4 w-4 text-orange-400" />
          <div className="overline">Live Board Election Turnout</div>
          <Badge variant="outline" className="text-[10px] border-orange-500/40 text-orange-300 bg-orange-500/10">
            {items.length} active
          </Badge>
        </div>
        <Link to="/admin/board-elections"
              className="text-xs text-orange-300 hover:text-orange-200 flex items-center gap-1"
              data-testid="turnout-manage-link">
          Manage <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <div className="space-y-3">
        {items.map((e) => (
          <TurnoutRow key={e.id} e={e} history={histMap[e.id] || []} />
        ))}
      </div>
    </Card>
  );
}


function TurnoutRow({ e, history }) {
  // Slope = last point pct - point ~2 hours ago
  const slope = (() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1];
    // find point roughly 2h ago
    const target = new Date(Date.now() - 2 * 3600e3);
    const earlier = history.filter((p) => new Date(p.at) <= target).slice(-1)[0]
                     || history[0];
    return (last.turnout_pct || 0) - (earlier.turnout_pct || 0);
  })();

  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] p-3"
          data-testid={`turnout-row-${e.id}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{e.title}</div>
          <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
            {e.status === "voting_open"
              ? <><Vote className="h-3 w-3 text-orange-400" /> Voting open</>
              : <><Users className="h-3 w-3 text-amber-400" /> Nominations open</>}
            {e.time_left && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{e.time_left}</span>}
            {slope !== null && (
              slope > 0 ? (
                <span className="flex items-center gap-0.5 text-emerald-400"
                       data-testid={`slope-${e.id}`}>
                  <TrendingUp className="h-3 w-3" /> +{slope.toFixed(1)}% / 2h
                </span>
              ) : slope < 0 ? null : (
                <span className="flex items-center gap-0.5 text-zinc-500"
                       data-testid={`slope-${e.id}`}>
                  <TrendingDown className="h-3 w-3" /> flat
                </span>
              )
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-semibold font-display tabular-nums text-orange-300"
                data-testid={`turnout-pct-${e.id}`}>
            {e.turnout_pct != null ? `${e.turnout_pct}%` : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {e.turnout_count}/{e.eligible} voted
          </div>
        </div>
      </div>
      {/* Sparkline — only render if we have ≥2 points */}
      {history.length >= 2 && (
        <div className="h-10 mt-1" data-testid={`sparkline-${e.id}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={history} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <Tooltip
                contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: 6, color: "white", fontSize: 11, padding: "4px 8px" }}
                labelFormatter={(v) => new Date(v).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                formatter={(v) => [`${v}%`, "Turnout"]}
              />
              <Line type="monotone" dataKey="turnout_pct" stroke="#F97316"
                     strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Progress bar */}
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-1">
        <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-500"
              style={{ width: `${Math.min(100, e.turnout_pct || 0)}%` }} />
      </div>
      {e.status === "voting_open" && (e.turnout_pct || 0) < 30 && (
        <div className="text-[11px] text-amber-300 mt-2 flex items-center gap-1">
          <MessageSquare className="h-3 w-3" />
          Low turnout — SMS blast fires automatically when ≤ 24h remain.
        </div>
      )}
    </div>
  );
}
